/**
 * Geo-gated consent regime (owner-approved 2026-07-11).
 *
 * Two regimes:
 * - `required`: opt-in jurisdictions (EU/EEA + UK + CH). The consent banner
 *   auto-shows and analytics/marketing stay OFF until the visitor decides.
 * - `optional`: everywhere else. No banner ambush; analytics/marketing default
 *   ON, and the visitor can opt out any time via the footer "Cookie
 *   preferences" link (a stored decision always wins over the default).
 *
 * The regime is stamped once by proxy.ts from Vercel's `x-vercel-ip-country`
 * header into a client-readable cookie. Unknown/missing country (dev,
 * unrecognized edge) falls back to `required` — the privacy-preserving side.
 */

export type ConsentRegime = "required" | "optional";

export const CONSENT_REGIME_COOKIE = "tls_consent_regime";

/** EU-27 + EEA (IS, LI, NO) + UK + CH. */
const OPT_IN_COUNTRIES = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR",
  "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL",
  "PL", "PT", "RO", "SK", "SI", "ES", "SE",
  "IS", "LI", "NO",
  "GB", "CH",
]);

export function regimeForCountry(country: string | null | undefined): ConsentRegime {
  if (!country) return "required"; // unknown → safe side
  return OPT_IN_COUNTRIES.has(country.toUpperCase()) ? "required" : "optional";
}

/**
 * Client-side cookie read. Missing cookie (first paint before proxy stamped
 * it, cookie-blocking clients) falls back to `required`.
 */
export function getClientConsentRegime(): ConsentRegime {
  if (typeof document === "undefined") return "required";
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${CONSENT_REGIME_COOKIE}=(required|optional)(?:;|$)`),
  );
  return (match?.[1] as ConsentRegime | undefined) ?? "required";
}
